import { useState, useRef } from 'react'
import { FlexboxProps } from 'styled-system'
import styled from 'styled-components'

import Chip, { Variant as ChipVariant } from '../Chip/Chip'
import NewChip from '../Chip/NewChip'
import { TextInputProps } from 'components/TextInput'

interface Props {
  variant?: ChipVariant
  readOnlyValues?: string[]
  values?: string[]
  onChange?: (values: string[]) => void
  isEditable?: boolean
  validation?: (value: string) => boolean
  errorMessage?: string
}

const ChipInput = ({
  variant = 'badge',
  readOnlyValues = [],
  values = [],
  onChange,
  isEditable = true,
  validation,
  errorMessage,
  alignItems = 'center',
  ...props
}: Props & FlexboxProps & Omit<TextInputProps, 'variant' | 'onChange'>) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [insert, setInsert] = useState('')
  const [insertError, setInsertError] = useState<string | null>(null)

  const handleAdd = () => {
    if (validation?.(insert) === false) {
      setInsertError(errorMessage ?? 'Invalid value')
      return
    }

    const added = [...values, insert]
    onChange?.(added)
    setInsert('')
  }

  const handleDismiss = (index: number) => () => {
    const removed = [...values]
    removed.splice(index, 1)
    onChange?.(removed)
  }

  return (
    <Container onClick={() => inputRef?.current?.focus()} alignItems={alignItems}>
      {readOnlyValues.map((item, idx) => (
        <Chip key={idx} variant={variant}>
          {item}
        </Chip>
      ))}
      {values.map((item, idx) => (
        <Chip key={idx} variant={variant} onDismiss={handleDismiss(idx)} isEditable={isEditable}>
          {item}
        </Chip>
      ))}
      {isEditable && (
        <NewChip
          ref={inputRef}
          variant={variant}
          value={insert}
          onChange={(e) => {
            setInsert(e.target.value)
            setInsertError(null)
          }}
          onPressEnter={handleAdd}
          placeholder=""
          error={insertError ?? undefined}
          height={props?.height}
          marginTop={insertError && props?.marginTop}
        />
      )}
    </Container>
  )
}

const Container = styled.div<FlexboxProps>`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: ${(props) => props?.alignItems};
  cursor: text;
  min-height: 2rem;

  input[type='text']:not(:focus) {
    width: 0;
  }
`

export default ChipInput
